import { HttpClient } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';
import { WebSocketJs } from '../websocket/websocket-js';

export interface JoinApplyRequest {
  request_id: string;
  project_id: string;
  applicantId: string;
  applicantFullName: string;
  userName: string;
  processing?: boolean;
}

export interface JoinRequestEvent {
  request_id: string;
  project_id?: string;
}

const NOTIFICATIONS_PATH = '/+/requests/+/notifications';

@Injectable({ providedIn: 'root' })
export class JoinApplyService {
  joinApplyRequests: JoinApplyRequest[] = [];
  displayJoinApplyModal = false;
  private subscribed = false;

  joinApproved$ = new Subject<JoinRequestEvent>();
  joinRejected$ = new Subject<JoinRequestEvent>();

  private http = inject(HttpClient);
  private ngZone = inject(NgZone);

  handleCreate = (data: any, notification: any): void => {
    this.ngZone.run(() => {
      const topic = notification?.event?.topic;
      if (!this.matchTopic(topic, NOTIFICATIONS_PATH)) {
        return;
      }
      const topicParts = (notification?.event?.topic || '').split('/').filter(Boolean);
      const project_id = topicParts[0] || '';
      const payload = this.normalizeJoinApplyPayload(data);
      if (payload?.event === 'join_apply') {
        this.addJoinApplyRequest(payload, notification?.event?.topic);
      } else if (payload?.event === 'join_approved') {
        this.joinApproved$.next({ request_id: payload.request_id, project_id });
      } else if (payload?.event === 'join_rejected') {
        this.joinRejected$.next({ request_id: payload.request_id });
      }
    });
  };

  handleUpdate = (data: any, notification: any): void => {
    this.ngZone.run(() => {
      const topic = notification?.event?.topic;
      if (!this.matchTopic(topic, NOTIFICATIONS_PATH)) {
        return;
      }
      const payload = this.normalizeJoinApplyPayload(data);
      if (payload?.event === 'join_resolved') {
        this.resolveJoinApplyByRequestId(payload.request_id);
      }
    });
  };

  subscribeToNotifications(webSocketJs: WebSocketJs): void {
    if (this.subscribed) {
      return;
    }
    webSocketJs.ref(
      NOTIFICATIONS_PATH,
      'joinApplyNotifications',
      this.handleCreate,
      this.handleUpdate,
      () => {},
    );
    this.subscribed = true;
  }

  ensureSubscribed(webSocketJs: WebSocketJs): void {
    if (this.subscribed) {
      return;
    }
    this.subscribeToNotifications(webSocketJs);
  }

  resetSubscription(): void {
    this.subscribed = false;
  }

  addJoinApplyRequest(data: any, topic?: string): void {
    this.ngZone.run(() => {
      this.addJoinApplyRequestInternal(data, topic);
    });
  }

  private addJoinApplyRequestInternal(data: any, topic?: string): void {
    const applicantId = data?.applicant?.id || data?.applicant?._id;
    if (!data?.request_id || !applicantId) {
      return;
    }

    const topicParts = (topic || '').split('/').filter(Boolean);
    const project_id = topicParts[0] || '';
    const applicantFullName = (data.applicant.fullName || '').trim()
      || [data.applicant.firstname, data.applicant.lastname].filter(Boolean).join(' ').trim();
    const userName = (data.user_name || '').trim();

    const isDuplicate = this.joinApplyRequests.some(
      (item) => item.request_id === data.request_id && item.applicantId === applicantId,
    );
    if (isDuplicate) {
      if (this.joinApplyRequests.length > 0) {
        this.displayJoinApplyModal = true;
      }
      return;
    }

    this.joinApplyRequests = [
      ...this.joinApplyRequests,
      {
        request_id: data.request_id,
        project_id,
        applicantId,
        applicantFullName,
        userName,
      },
    ];
    this.displayJoinApplyModal = true;
  }

  resolveJoinApplyByRequestId(requestId: string): void {
    this.ngZone.run(() => {
      this.joinApplyRequests = this.joinApplyRequests.filter(
        (item) => item.request_id !== requestId,
      );
      if (this.joinApplyRequests.length === 0) {
        this.displayJoinApplyModal = false;
      }
    });
  }

  closeJoinApplyModal(): void {
    this.displayJoinApplyModal = false;
  }

  setJoinApplyRequestProcessing(requestId: string, applicantId: string, processing: boolean): void {
    this.joinApplyRequests = this.joinApplyRequests.map((item) => {
      if (item.request_id === requestId && item.applicantId === applicantId) {
        return { ...item, processing };
      }
      return item;
    });
  }

  approveJoinApply(item: JoinApplyRequest): Observable<unknown> {
    return this.sendJoinResponse(item.request_id, item.applicantId, true, item.project_id);
  }

  rejectJoinApply(item: JoinApplyRequest): Observable<unknown> {
    return this.sendJoinResponse(item.request_id, item.applicantId, false, item.project_id);
  }

  private sendJoinResponse(requestId: string, applicantId: string, accepted: boolean, projectId: string): Observable<unknown> {
    const body = { accepted, applicant_id: applicantId };
    const url = `${environment.oldApi}/${projectId}/requests/${requestId}/join-response`;
    return this.http.post(url, body);
  }

  private normalizeJoinApplyPayload(data: any): any {
    if (!data) {
      return null;
    }
    if (data.event) {
      return data;
    }
    if (data.data?.event) {
      return data.data;
    }
    return data;
  }

  private matchTopic(topic: string, pattern: string): boolean {
    if (!topic) {
      return false;
    }
    const topicParts = topic.split('/').filter(Boolean);
    const patternParts = pattern.split('/').filter(Boolean);
    if (topicParts.length !== patternParts.length) {
      return false;
    }
    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i] === '+') {
        continue;
      }
      if (patternParts[i] !== topicParts[i]) {
        return false;
      }
    }
    return true;
  }
}
